import { useNavigate } from "@tanstack/react-router";
import { InboxIcon, XIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";

import {
  AGENT_STRIP_MAX_DOTS,
  projectAgentStrip,
  type AgentStripDot,
  type AgentStripProjection,
  decidedElsewhere,
} from "~/agentStrip";
import {
  ANSWER_HOLD_MS,
  OPEN_TO_APPROVE,
  STOPPED_NO_KEY,
  keysMayAnswer,
  rereadGateForAnswer,
  stripKeyAction,
} from "~/agentStripKeys";
import type { GateDecisionOutcome, GateInstanceRow } from "~/neucleos/gateInstancesClient";
import { HitlPane, decideGateCard, gateStoppedHere } from "./HitlPane";
import type { InboxBoardCommand } from "./InboxBoard";
import { scopeThreadRef, scopedThreadKey } from "@cleo/client-runtime/environment";
import { useNotificationSnapshots } from "~/neucleos/notificationObserver";
import {
  acknowledgeCompletion,
  completionCursor,
  currentCompletionScope,
  useCompletionReadsStore,
} from "~/neucleos/threadCompletionReads";
import { openWorldNotice } from "~/neucleos/openWorldNotice";
import { useWorldRetiredSessions } from "~/neucleos/retiredWorldSessions";
import { useNotificationSources } from "~/neucleos/useNotificationSources";
import { useWorldInboxUnread } from "~/neucleos/useWorldInboxUnread";
import { openWorldViewInbox, openWorldViewInboxRail } from "~/neucleos/worldViewController";
import {
  allModeActive,
  localModeActive,
  worldDisplayName,
  type NeucleosWorldSummary,
} from "~/neucleos/worldSelection";
import { enterWorld, useNeucleosWorldStore } from "~/neucleos/worldStore";
import { ROOT_SESSION_STATUS_LABELS, rootSessionStatusTextClass } from "~/rootSessionStrip";
import { useRootSessionThreadInputs } from "~/rootSessionStripContainer";
import { useWorldConversationStripInputs } from "~/neucleos/worldConversationStrip";
import { useNowMinute } from "~/hooks/useNowMinute";
import { cn } from "~/lib/utils";
import { SESSION_NOTIFICATION_TITLES } from "~/notifications/sessionNotifications";
import { formatRelativeTimeLabel } from "~/timestampFormat";
import { SessionRing } from "./SessionRing";
import {
  Popover,
  PopoverClose,
  PopoverDescription,
  PopoverPopup,
  PopoverTitle,
  PopoverTrigger,
} from "./ui/popover";

// The agent strip (agent-strip phase 1, 6 October 2026, the principal: "maybe
// in the header area beside the search ... then maybe we remove the tab that
// we currently have that's constantly open"). One ring per live session sits
// beside Search & commands; the Inbox drops down from it when summoned. The
// ring's shape carries the state (an arc turns while working, a centre pip
// marks needs-you or a new result), and its hue comes from the one shared
// status table, so color is never the only signal.
//
// A waiting decision opens the Inbox's own card (HitlPane, moved unchanged out
// of the board for this), deciding through the card's one decision path
// (decideGateCard), never a second copy of either.

/** A gate still waiting on the person: the board's own "awaiting" rule. */
export function decisionAwaitsPerson(row: GateInstanceRow): boolean {
  return row.state === "CREATED" && row.humanDecides && row.effectStatus !== "cancelled";
}

/**
 * W-7d0032b2: newest first. The newest decision is almost always the one the
 * person just asked Cleo for, the reason they opened the popover, so it leads
 * and stale ones settle below. Equal times keep the order the World gave.
 */
export function newestDecisionsFirst(
  decisions: ReadonlyArray<AgentStripDecision>,
): AgentStripDecision[] {
  const at = (decision: AgentStripDecision) =>
    Number.isFinite(decision.row.createdAt) ? decision.row.createdAt : 0;
  return decisions
    .map((decision, index) => ({ decision, index }))
    .sort((left, right) => at(right.decision) - at(left.decision) || left.index - right.index)
    .map(({ decision }) => decision);
}

export interface AgentStripDecision {
  readonly worldKey: string;
  readonly row: GateInstanceRow;
}

/**
 * A Finished card's recap, from words the Desk already has (agent-strip
 * phase 2; no model call): the session-notification wording shared with the
 * system notifications, and how long ago it finished.
 */
/**
 * E on a Finished card (#1373): the person marks it read. It writes the same
 * private view receipt a result the person has seen writes
 * (acknowledgeCompletion), at the run's own finish time, so the card and its
 * ring go quiet as if it had been opened. No World or Goal state is written. A
 * World run while signed out has no receipt scope, and nothing is written.
 */
export function clearFinished(
  dot: Pick<AgentStripDot, "environmentId" | "threadId" | "worldKey" | "finishedAt">,
): boolean {
  const scope = currentCompletionScope(dot.worldKey);
  if (scope === undefined || !dot.finishedAt) return false;
  const threadKey = scopedThreadKey(
    scopeThreadRef(dot.environmentId as never, dot.threadId as never),
  );
  acknowledgeCompletion(scope, threadKey, dot.finishedAt);
  // Cleared only if the store kept it (a full store keeps its old receipts and
  // drops the new one; Codex on #1373): the cursor now reaches the finish time.
  const cursor = completionCursor(useCompletionReadsStore.getState().buckets, scope, threadKey);
  return cursor !== undefined && Date.parse(cursor) >= Date.parse(dot.finishedAt);
}

/** A Finished card's own id: its environment and thread, which cannot collide as a joined key can. */
const finishedCardId = (dot: Pick<AgentStripDot, "environmentId" | "threadId">) =>
  JSON.stringify([dot.environmentId, dot.threadId]);

export function finishedRecap(dot: Pick<AgentStripDot, "status" | "finishedAt">): string {
  const words = SESSION_NOTIFICATION_TITLES[dot.status === "failed" ? "failed" : "completed"];
  const when = dot.finishedAt ? formatRelativeTimeLabel(dot.finishedAt) : null;
  return when ? `${words} · ${when}` : words;
}

/** How the strip names itself to a screen reader: every count that matters. */
export function agentStripLabel(
  strip: AgentStripProjection,
  decisions: number,
  messages = 0,
  unreadable = false,
): string {
  const parts: string[] = [];
  if (unreadable) parts.push("some decisions could not be read");
  if (decisions > 0) parts.push(`${decisions} to decide`);
  if (messages > 0) parts.push(`${messages} unread ${messages === 1 ? "message" : "messages"}`);
  if (strip.needsCount > 0)
    parts.push(`${strip.needsCount} ${strip.needsCount === 1 ? "needs" : "need"} you`);
  const working = strip.sessions.filter((dot) => dot.state === "working").length;
  if (working > 0) parts.push(`${working} working`);
  if (strip.doneCount > 0) parts.push(`${strip.doneCount} finished`);
  return parts.length === 0 ? "Inbox, all clear" : `Inbox, ${parts.join(", ")}`;
}

export function AgentStrip() {
  const navigate = useNavigate();
  const deskThreads = useRootSessionThreadInputs();
  // W-1c87244d: the person's conversations kept in the World stand in the strip beside this Desk's threads.
  const worldConversations = useWorldConversationStripInputs();
  const threads = useMemo(() => [...deskThreads, ...worldConversations], [deskThreads, worldConversations]);
  const worlds = useNeucleosWorldStore((state) => state.worlds);
  const signedIn = useNeucleosWorldStore((state) => state.signedIn);
  const activeWorldKey = useNeucleosWorldStore((state) => state.activeWorldKey);
  // The one thread scope every list uses (selectWorldThreadScope): Local is
  // also the stance of a signed-out person or one with no Worlds.
  const local = useNeucleosWorldStore((state) =>
    localModeActive({
      signedIn: state.signedIn,
      worldCount: state.worlds.length,
      selection: state.selection,
    }),
  );
  const all = useNeucleosWorldStore((state) => allModeActive(state.selection));
  const [open, setOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // W-ea4c5090: a World session archived (retired) in the World leaves the
  // strip and the Finished cards, though this Desk's thread stays unarchived.
  const nowMinute = useNowMinute();
  const retiredCandidates = useMemo(
    () =>
      threads.filter(
        (thread) =>
          thread.status != null && thread.archivedAt === null && thread.snoozedUntil === null,
      ),
    [threads],
  );
  const retired = useWorldRetiredSessions(retiredCandidates, Date.parse(`${nowMinute}:00.000Z`));
  const strip = useMemo(
    () =>
      projectAgentStrip(threads, { activeWorldKey, local, all }, AGENT_STRIP_MAX_DOTS, retired),
    [threads, activeWorldKey, local, all, retired],
  );
  const worldKeys = signedIn ? worlds.map((world) => world.worldKey) : [];
  const feeds = useNotificationSnapshots(worldKeys);
  const pending = feeds.reduce((sum, feed) => sum + (feed.value?.pendingGateCount ?? 0), 0);
  const sources = useNotificationSources(worldKeys, open, false, feeds);
  const decisions = newestDecisionsFirst(
    sources.rows.flatMap((source) =>
      source.gates.ok
        ? source.gates.rows
            .filter(decisionAwaitsPerson)
            .map((row) => ({ worldKey: source.worldKey, row }))
        : [],
    ),
  );
  // A World whose decisions are not in hand is named, never shown as
  // "nothing waiting": its feed refused or lacks its gates, or its decide
  // lane refused the read (the bell's own "unavailable" rule).
  const unreadable = [
    ...new Set([
      ...feeds
        .filter((feed) => feed.error || (feed.value && !feed.value.gates))
        .map((feed) => feed.worldKey),
      ...sources.rows.filter((source) => !source.gates.ok).map((source) => source.worldKey),
    ]),
  ];
  // A World whose waiting decisions run past the first page (cancelled rows can
  // fill it) is named with a door to its full list, never "nothing waiting".
  const morePages = sources.rows
    .filter((source) => source.gates.ok && Boolean(source.gates.nextCursor))
    .map((source) => source.worldKey);
  const stillReading = sources.loading || feeds.some((feed) => !feed.error && !feed.value);
  const decisionState = useDecisionState();
  // People's unread messages in the entered World: the count the retired
  // pinned Inbox tab carried, so retiring the tab loses no signal.
  const messages = useWorldInboxUnread(signedIn ? activeWorldKey : null);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setError(null);
      // Rule 3: closing the popover during a hold sends nothing.
      decisionState.cancelHold();
    }
  };

  // One-key answers (agent-strip phase 3). The handler sits inside the popover,
  // so it hears keys only while focus is in the popover the person opened
  // (rule 2); the popover never opens or takes focus by itself.
  const onPopoverKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const action = stripKeyAction(event);
    if (action === null) return;
    if (action.kind === "undo") {
      if (!decisionState.held) return; // Esc with nothing held closes the popover as ever.
      event.preventDefault();
      event.stopPropagation();
      decisionState.cancelHold();
      return;
    }
    if (action.kind === "clear") {
      // Only the Finished card that has focus is marked read; E anywhere else does nothing.
      // The card is found by its own collision-free id: two threads' keys can
      // encode the same ("env" + "part:t", "env:part" + "t"), so neither the
      // key nor a place among the cards may name it (Codex on #1373).
      const card =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>("[data-agent-strip-finished-id]")
          : null;
      const dot = card
        ? strip.finished.find((item) => finishedCardId(item) === card.dataset.agentStripFinishedId)
        : undefined;
      if (!dot || !clearFinished(dot)) return;
      const cards = [
        ...event.currentTarget.querySelectorAll<HTMLElement>("[data-agent-strip-finished-id]"),
      ];
      const at = cards.indexOf(card!);
      event.preventDefault();
      // Focus moves to the next finished card (or the one before), else the popover itself.
      (cards[at + 1] ?? cards[at - 1] ?? event.currentTarget).focus({ preventScroll: true });
      return;
    }
    if (action.kind === "move") {
      if (decisions.length === 0) return;
      event.preventDefault();
      const at = decisions.findIndex((decision) => decisionKey(decision) === decisionState.chosen);
      const next = at < 0 ? 0 : Math.min(decisions.length - 1, Math.max(0, at + action.delta));
      const target = decisions[next]!;
      if (decisionKey(target) !== decisionState.chosen) decisionState.choose(decisionKey(target));
      return;
    }
    // An answer acts only on the card the person has open, and only when that
    // card may take a key: never Touch ID, never a card whose own Approve is
    // unavailable (rule 6). The card's button state is the card's own verdict.
    const open = decisions.find((decision) => decisionKey(decision) === decisionState.chosen);
    if (!open) return;
    const key = decisionKey(open);
    if (decisionState.inFlight.has(key)) return;
    if (!keysMayAnswer(open.row, decisionState.available.get(key) === true)) return;
    event.preventDefault();
    // Focus rests on the card itself, which is never disabled, so only the
    // person moving focus out of the popover ends the hold (rule 2).
    event.currentTarget
      .querySelector<HTMLElement>(
        `[data-agent-strip-card="${CSS.escape(open.row.gateInstanceKey)}"]`,
      )
      ?.focus({ preventScroll: true });
    decisionState.hold(key, open.worldKey, open.row, action.outcome);
  };
  // Rule 2 and 3: focus leaving the popover (to the composer, the trigger,
  // anywhere) during a hold ends it with nothing sent.
  const onPopoverBlur = (event: ReactFocusEvent<HTMLDivElement>) => {
    const to = event.relatedTarget;
    if (to instanceof Node && event.currentTarget.contains(to)) return;
    if (decisionState.held) decisionState.cancelHold();
  };

  const openSession = (dot: AgentStripDot) => {
    changeOpen(false);
    if (dot.worldKey !== null && dot.sessionId !== null) {
      if (dot.worldKey !== activeWorldKey) {
        enterWorld(dot.worldKey, { sessionId: dot.sessionId });
        return;
      }
      void navigate({ to: "/session/$sessionId", params: { sessionId: dot.sessionId } } as never);
      return;
    }
    void navigate({
      to: "/$environmentId/$threadId",
      params: { environmentId: dot.environmentId, threadId: dot.threadId },
    } as never);
  };

  // P1-2: a hold whose card is no longer shown (a refresh, a decision made
  // elsewhere, a refused read) ends unsent: nothing is sent for a card the
  // person cannot see.
  const heldKey = decisionState.held?.key ?? null;
  const shownKeys = decisions.map(decisionKey).join("\n");
  const { cancelHold } = decisionState;
  useEffect(() => {
    if (heldKey !== null && !shownKeys.split("\n").includes(heldKey)) cancelHold();
  }, [heldKey, shownKeys, cancelHold]);
  // A card whose own Approve becomes unavailable during a hold (its change
  // expired, its facts no longer shown) ends the hold unsent.
  const heldAvailable = heldKey === null ? null : decisionState.available.get(heldKey);
  useEffect(() => {
    if (heldKey !== null && heldAvailable === false) cancelHold();
  }, [heldKey, heldAvailable, cancelHold]);

  const openDecisions = async (worldKey: string, gateInstanceKey?: string) => {
    decisionState.cancelHold();
    if (opening) return;
    setOpening(true);
    setError(null);
    try {
      await openWorldNotice(
        worldKey,
        gateInstanceKey === undefined
          ? { kind: "decisions" }
          : { kind: "decisions", id: gateInstanceKey },
        navigate,
      );
      changeOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The decisions could not be opened.");
    } finally {
      setOpening(false);
    }
  };

  const openFullInbox = () => {
    // The seated room canvas fronts its Inbox in place; with no canvas seated,
    // the World's decisions door seats one first.
    if (openWorldViewInbox()) {
      changeOpen(false);
      return;
    }
    if (activeWorldKey !== null) void openDecisions(activeWorldKey);
  };

  const openMessages = () => {
    if (openWorldViewInboxRail("world")) {
      changeOpen(false);
      return;
    }
    openFullInbox();
  };

  const feedUnreadable = feeds.some((feed) => feed.error || (feed.value && !feed.value.gates));
  // Finished work has its own cards; Sessions lists what is still running or waiting.
  // A conversation kept in the World that waits on a card is listed once, as that card under Decisions (W-fa2578b8).
  const active = strip.sessions.filter((dot) => !strip.finished.includes(dot) && !decidedElsewhere(dot));
  const label = agentStripLabel(strip, pending, messages, feedUnreadable);

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger
        aria-label={label}
        className="ncl-ticker-glass flex h-8 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-muted-foreground hover:text-foreground"
        data-shell-utility="agent-strip"
        data-agent-strip-needs={strip.needsCount}
        data-agent-strip-pending={pending}
        title={label}
      >
        {strip.dots.length === 0 ? (
          <InboxIcon aria-hidden="true" className="size-3.5" />
        ) : (
          strip.dots.map((dot) => (
            <SessionRing
              className="size-3.5"
              data-agent-strip-dot={dot.key}
              data-agent-strip-state={dot.state}
              key={dot.key}
              status={dot.status}
            />
          ))
        )}
        {strip.overflow > 0 ? (
          <span aria-hidden="true" className="text-[10px] tabular-nums">
            +{strip.overflow}
          </span>
        ) : null}
        {messages > 0 ? (
          <span
            aria-hidden="true"
            className="size-1.5 shrink-0 rounded-full bg-primary"
            data-agent-strip-messages={messages}
          />
        ) : null}
        {pending > 0 ? (
          <span
            aria-hidden="true"
            className="text-[10px] font-medium tabular-nums text-warning"
            data-agent-strip-decisions={pending}
          >
            {pending}
          </span>
        ) : null}
        {feedUnreadable ? (
          <span
            aria-hidden="true"
            className="text-[10px] font-medium text-warning"
            data-agent-strip-unreadable-mark=""
          >
            !
          </span>
        ) : null}
      </PopoverTrigger>
      <PopoverPopup
        align="center"
        sideOffset={10}
        className="w-[min(420px,calc(100vw-24px))]"
        viewportClassName="max-h-[min(70vh,640px)] overflow-y-auto"
      >
        <div
          className="outline-none"
          data-agent-strip-keys=""
          onBlur={onPopoverBlur}
          onKeyDown={onPopoverKey}
          tabIndex={-1}
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            <PopoverTitle>Inbox</PopoverTitle>
            <PopoverClose
              aria-label="Close Inbox"
              className="rounded px-2 py-1 text-xs text-primary hover:bg-accent focus-visible:outline-2"
            >
              <XIcon className="size-4" />
            </PopoverClose>
          </div>
          <PopoverDescription className="mb-4">{label.replace(/^Inbox, /, "")}.</PopoverDescription>
          {error ? (
            <p role="alert" className="mb-3 text-sm text-warning">
              {error}
            </p>
          ) : null}
          {signedIn ? (
            <AgentStripDecisions
              decisions={decisions}
              loading={stillReading}
              onOpen={(worldKey, gateInstanceKey) => void openDecisions(worldKey, gateInstanceKey)}
              opening={opening}
              morePages={morePages}
              refusals={unreadable}
              state={decisionState}
              worlds={worlds}
            />
          ) : null}
          {messages > 0 ? (
            <button
              className="mb-3 block w-full rounded-md p-2 text-left hover:bg-accent"
              data-agent-strip-open-messages=""
              onClick={openMessages}
              type="button"
            >
              <span className="block text-sm">
                {messages} unread {messages === 1 ? "message" : "messages"}
              </span>
              <span className="block text-xs text-muted-foreground">Open messages</span>
            </button>
          ) : null}
          {strip.finished.length > 0 ? (
            <section
              aria-label="Finished"
              className={cn("space-y-1", signedIn && "mt-4 border-t border-border pt-3")}
            >
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="text-sm font-medium">Finished</h3>
                <span
                  className="text-[11px] text-muted-foreground"
                  data-agent-strip-finished-hint=""
                >
                  E marks one read
                </span>
              </div>
              {strip.finished.map((dot) => (
                <button
                  className="flex w-full items-start gap-2 rounded-md border border-border/50 p-2 text-left hover:bg-accent"
                  data-agent-strip-finished={dot.key}
                  data-agent-strip-finished-id={finishedCardId(dot)}
                  key={finishedCardId(dot)}
                  onClick={() => openSession(dot)}
                  type="button"
                >
                  <SessionRing className="mt-0.5 size-4" status={dot.status} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{dot.label}</span>
                    <span
                      className={cn("block text-xs", rootSessionStatusTextClass(dot.status))}
                      data-agent-strip-finished-recap=""
                    >
                      {finishedRecap(dot)}
                    </span>
                  </span>
                </button>
              ))}
            </section>
          ) : null}
          <section
            aria-label="Sessions"
            className={cn(
              "space-y-1",
              (signedIn || strip.finished.length > 0) && "mt-4 border-t border-border pt-3",
            )}
          >
            <h3 className="text-sm font-medium">Sessions</h3>
            {active.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {strip.finished.length > 0
                  ? "Nothing else is running or waiting."
                  : "All clear. No session is working or waiting on you."}
              </p>
            ) : (
              active.map((dot) => (
                <button
                  className="flex w-full items-center gap-2 rounded-md p-2 text-left hover:bg-accent"
                  data-agent-strip-session={dot.key}
                  key={dot.key}
                  onClick={() => openSession(dot)}
                  type="button"
                >
                  <SessionRing className="size-4" status={dot.status} />
                  <span className="min-w-0 flex-1 truncate text-sm">{dot.label}</span>
                  <span className={cn("shrink-0 text-xs", rootSessionStatusTextClass(dot.status))}>
                    {ROOT_SESSION_STATUS_LABELS[dot.status]}
                  </span>
                </button>
              ))
            )}
          </section>
          {activeWorldKey !== null ? (
            <div className="mt-4 border-t border-border pt-3">
              <button
                className="rounded px-2 py-1 text-xs text-primary hover:bg-accent focus-visible:outline-2 disabled:opacity-50"
                data-agent-strip-full-inbox=""
                disabled={opening}
                onClick={openFullInbox}
                type="button"
              >
                Open full Inbox
              </button>
            </div>
          ) : null}
        </div>
      </PopoverPopup>
    </Popover>
  );
}

/**
 * W-77f2f204 on the popover: the decision stays in view. The open card is
 * shorter than the popover's own scroll area (min(70vh,640px)), and opening it
 * scrolls the whole card into view, so Approve and Decline (HitlPane's
 * non-scrolling footer) are on screen without scrolling. Found in #1307's run 2,
 * where they sat partly below the popover's edge.
 */
export const DECISION_CARD_MAX_HEIGHT = "max-h-[min(50vh,400px)]";

function DecisionCardFrame({
  gateInstanceKey,
  children,
}: {
  readonly gateInstanceKey: string;
  readonly children: ReactNode;
}) {
  const frame = useRef<HTMLDivElement>(null);
  useEffect(() => {
    frame.current?.scrollIntoView({ block: "nearest" });
    // A card opened by J/K takes the keyboard focus the popover already had
    // (the previous card left with it). Focus elsewhere (the composer) is
    // never taken.
    const active = document.activeElement;
    const popover = frame.current?.closest("[data-agent-strip-keys]");
    if (
      frame.current &&
      popover &&
      (active === document.body || (active && popover.contains(active)))
    )
      frame.current.focus({ preventScroll: true });
  }, []);
  return (
    <div
      className={cn(
        "mt-1 flex min-h-0 flex-col rounded-md border border-border/60 p-2",
        DECISION_CARD_MAX_HEIGHT,
      )}
      data-agent-strip-card={gateInstanceKey}
      ref={frame}
      tabIndex={-1}
    >
      {children}
    </div>
  );
}

const decisionKey = (decision: AgentStripDecision) =>
  `${decision.worldKey}:${decision.row.gateInstanceKey}`;

/**
 * The popover's decisions, held by the always-mounted strip so closing the
 * popover mid-decision loses neither the in-flight guard nor the lane's
 * answer: each card has its own pending flag and its own refusal, so one
 * card's answer never lands on another.
 */
function useDecisionState() {
  const [chosen, setChosen] = useState<string | null>(null);
  const [inFlight, setInFlight] = useState<ReadonlySet<string>>(() => new Set());
  const [refused, setRefused] = useState<ReadonlyMap<string, string>>(() => new Map());
  const claimed = useRef(new Set<string>());
  const [held, setHeld] = useState<AnswerHold | null>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The hold as the timer and the listeners see it, set wherever `held` is.
  const heldRef = useRef<AnswerHold | null>(null);
  // Each open card's own verdict on whether its Approve may take a key (HitlPane).
  const [available, setAvailableMap] = useState<ReadonlyMap<string, boolean>>(() => new Map());
  // Per card: false while its Approve is unavailable, else when it expires (null: never on its own).
  const approvable = useRef(new Map<string, number | null | false>());
  // The last thing a card said about a hold that ended without sending.
  const [undone, setUndone] = useState<string | null>(null);
  const release = (key: string) => {
    claimed.current.delete(key);
    setInFlight((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  };
  const refuse = (key: string, reason: string) =>
    setRefused((current) => new Map(current).set(key, reason));
  // Rule 3: the hold is the client's alone. Cancelling it clears the timer
  // and sends nothing; the card stays as it was.
  const cancelHold = useCallback(() => {
    if (holdTimer.current !== null) clearTimeout(holdTimer.current);
    holdTimer.current = null;
    const was = heldRef.current;
    heldRef.current = null;
    setHeld(null);
    if (was) {
      claimed.current.delete(was.key);
      if (was.phase === "checking")
        setInFlight((current) => {
          const next = new Set(current);
          next.delete(was.key);
          return next;
        });
      setUndone(was.key);
    }
  }, []);
  // The latch (keyed approve, after Codex's remount finding on #1351): a card
  // saying its Approve is unavailable ends that card's hold there and then,
  // before a later "available" in the same update (a remount reports false,
  // then true) can hide it. A hold that ended is never revived; the person
  // presses again.
  const setAvailable = useCallback(
    (key: string, value: boolean, until: number | null = null) => {
      approvable.current.set(key, value ? until : false);
      if (!value && heldRef.current?.key === key) cancelHold();
      setAvailableMap((current) =>
        current.get(key) === value ? current : new Map(current).set(key, value),
      );
    },
    [cancelHold],
  );
  // What the card last said, read at the moment a keyed approve would leave
  // (#1365 round 1): its Approve still available, and its prepared change not
  // yet expired, even in the instant before the card redraws at that expiry.
  const approvableNow = (key: string) => {
    const until = approvable.current.get(key);
    return until === null || (typeof until === "number" && Date.now() < until);
  };
  // The window losing focus (another app, the Desk hiding) or the strip
  // unmounting (the Desk quitting) also ends a hold with nothing sent.
  useEffect(() => {
    const onBlur = () => {
      if (heldRef.current) cancelHold();
    };
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("blur", onBlur);
      if (holdTimer.current !== null) clearTimeout(holdTimer.current);
      // The Desk quitting (the strip unmounting) ends a hold, including one
      // whose gate is being read again: nothing is sent after this.
      heldRef.current = null;
    };
  }, [cancelHold]);
  const choose = (key: string) => {
    if (heldRef.current) cancelHold();
    setUndone(null);
    setChosen((current) => (current === key ? null : key));
  };
  // The card's one decision path, for an answer already claimed by `key`.
  const send = (key: string, worldKey: string, command: InboxBoardCommand) => {
    if (command.type !== "decideGateInstance") return;
    setInFlight((current) => new Set(current).add(key));
    setRefused((current) => {
      const next = new Map(current);
      next.delete(key);
      return next;
    });
    void decideGateCard(worldKey, command)
      .then((outcome) => {
        release(key);
        if (!outcome.ok) {
          setRefused((current) => new Map(current).set(key, outcome.reason));
          return;
        }
        setChosen((current) => (current === key ? null : current));
      })
      .catch(() => {
        release(key);
        refuse(
          key,
          "Your decision could not be confirmed. Open the request in its World to check it.",
        );
      });
  };
  // A click on the card's own Approve or Decline.
  const decide = (key: string, worldKey: string, command: InboxBoardCommand) => {
    // A click on the card's own button during a key's hold is the person's
    // newer word: the hold ends unsent and the click decides.
    if (heldRef.current?.key === key) cancelHold();
    setUndone(null);
    if (command.type !== "decideGateInstance" || claimed.current.has(key)) return;
    claimed.current.add(key);
    send(key, worldKey, command);
  };
  /**
   * An answer by key. Rule 1: it goes only through `send` (decideGateCard).
   * A held or repeated key, a second key while one waits, or a key on a card
   * already deciding does nothing: the key is claimed for the whole hold.
   * Rule 4: when the hold ends the gate is read again, and a changed, decided,
   * cancelled or missing request is refused with its reason, with nothing sent.
   */
  const hold = (
    key: string,
    worldKey: string,
    row: GateInstanceRow,
    outcome: GateDecisionOutcome,
  ) => {
    if (heldRef.current || claimed.current.has(key)) return;
    claimed.current.add(key);
    setUndone(null);
    setRefused((current) => {
      const next = new Map(current);
      next.delete(key);
      return next;
    });
    const next: AnswerHold = {
      key,
      worldKey,
      outcome,
      gateInstanceKey: row.gateInstanceKey,
      phase: "holding",
    };
    heldRef.current = next;
    setHeld(next);
    holdTimer.current = setTimeout(() => {
      holdTimer.current = null;
      if (heldRef.current !== next) return;
      // The 2 s are over; the hold now waits on the re-read (rule 4) and can
      // still be cancelled by everything that cancels it during the 2 s. The
      // card shows it as checking, its buttons and keys are off.
      const checking: AnswerHold = { ...next, phase: "checking" };
      heldRef.current = checking;
      setHeld(checking);
      setInFlight((current) => new Set(current).add(key));
      void rereadGateForAnswer(worldKey, row)
        .then((reread) => {
          // Cancelled while the gate was being read: nothing is sent. The
          // cancel already freed the card, and a newer answer may hold it now,
          // so this late read releases nothing.
          if (heldRef.current !== checking) return;
          heldRef.current = null;
          setHeld(null);
          if (!reread.ok) {
            release(key);
            refuse(key, reread.reason);
            return;
          }
          if (outcome === "approved" && !approvableNow(key)) {
            release(key);
            refuse(key, "This request can no longer be approved here. Nothing was sent.");
            return;
          }
          send(key, worldKey, {
            type: "decideGateInstance",
            gateInstanceKey: reread.row.gateInstanceKey,
            targetDigest: reread.row.targetDigest,
            outcome,
          });
        })
        .catch(() => {
          if (heldRef.current !== checking) return; // cancelled: the cancel released the card
          heldRef.current = null;
          setHeld(null);
          release(key);
          refuse(key, "The request could not be read again, so nothing was sent.");
        });
    }, ANSWER_HOLD_MS);
  };
  return {
    chosen,
    choose,
    decide,
    hold,
    held,
    cancelHold,
    inFlight,
    refused,
    available,
    setAvailable,
    undone,
  };
}

interface AnswerHold {
  readonly key: string;
  readonly worldKey: string;
  readonly gateInstanceKey: string;
  readonly outcome: GateDecisionOutcome;
  /** "holding": the 2 s; "checking": the gate is being read again. Both end unsent on any cancel. */
  readonly phase: "holding" | "checking";
}

type DecisionState = ReturnType<typeof useDecisionState>;

/** The Inbox's own card, with a stable per-card report of whether its Approve may take a key. */
function StripCard({
  cardKey,
  setAvailable,
  ...pane
}: Omit<Parameters<typeof HitlPane>[0], "onApproveAvailableChange"> & {
  readonly cardKey: string;
  readonly setAvailable: (key: string, value: boolean, until?: number | null) => void;
}) {
  const onApproveAvailableChange = useCallback(
    (value: boolean, until?: number | null) => setAvailable(cardKey, value, until),
    [cardKey, setAvailable],
  );
  return <HitlPane {...pane} onApproveAvailableChange={onApproveAvailableChange} />;
}

/**
 * What the keys do on this card, or the hold in progress with its way back.
 * One live region that stays mounted, so each change is announced: the hold,
 * the send, an undo ("Nothing was sent"), or what the keys do here.
 */
function AnswerKeyHint({
  held,
  checking,
  sending,
  undone,
  keyable,
  stopped,
  onUndo,
}: {
  readonly held: GateDecisionOutcome | null;
  readonly checking: boolean;
  readonly sending: boolean;
  readonly undone: boolean;
  readonly keyable: boolean;
  /** A card Cleo was stopped on: only Decline is left, so it says that, not "Open to approve". */
  readonly stopped: boolean;
  readonly onUndo: () => void;
}) {
  const text =
    held !== null
      ? `${held === "approved" ? "Approving" : "Declining"} in ${ANSWER_HOLD_MS / 1000} s.`
      : checking
        ? "Checking the request before sending…"
        : sending
          ? "Sending your decision…"
          : undone
            ? "Undone. Nothing was sent."
            : keyable
              ? "Keys: 1 approve · 2 decline · J / K next"
              : stopped
                ? STOPPED_NO_KEY
                : OPEN_TO_APPROVE;
  return (
    <div className="mt-2 flex items-center gap-2">
      <p
        aria-live="polite"
        className={cn(
          "text-xs",
          held !== null ? "font-medium text-foreground" : "text-[11px] text-muted-foreground",
        )}
        data-agent-strip-hold={held ?? undefined}
        data-agent-strip-key-hint={held === null ? "" : undefined}
        role="status"
      >
        {text}
      </p>
      {held !== null || checking ? (
        <button
          className="rounded px-1.5 py-0.5 text-xs text-primary hover:bg-accent"
          data-agent-strip-undo=""
          onClick={(event) => {
            // Focus stays with the card, so its keys still work after the undo.
            const frame = event.currentTarget.closest<HTMLElement>("[data-agent-strip-card]");
            onUndo();
            frame?.focus();
          }}
          type="button"
        >
          Esc to undo
        </button>
      ) : null}
    </div>
  );
}

function AgentStripDecisions({
  decisions,
  loading,
  refusals,
  worlds,
  opening,
  onOpen,
  state,
  morePages,
}: {
  readonly morePages: ReadonlyArray<string>;
  readonly decisions: ReadonlyArray<AgentStripDecision>;
  readonly loading: boolean;
  readonly refusals: ReadonlyArray<string>;
  readonly worlds: ReadonlyArray<NeucleosWorldSummary>;
  readonly opening: boolean;
  readonly onOpen: (worldKey: string, gateInstanceKey?: string) => void;
  readonly state: DecisionState;
}) {
  // One card open at a time; the row leaves the list when the gate bus
  // re-reads the lane's answer.
  return (
    <section aria-label="Decisions" className="space-y-1">
      <h3 className="text-sm font-medium">Decisions</h3>
      {loading ? (
        <p role="status" className="text-xs text-muted-foreground">
          Checking decisions…
        </p>
      ) : null}
      {decisions.map((decision) => {
        const key = decisionKey(decision);
        const expanded = state.chosen === key;
        const pending = state.inFlight.has(key);
        return (
          <article key={key}>
            <button
              aria-expanded={expanded}
              className="block w-full rounded-md p-2 text-left hover:bg-accent disabled:opacity-60"
              data-agent-strip-decision={decision.row.gateInstanceKey}
              disabled={pending}
              onClick={() => state.choose(key)}
              type="button"
            >
              <span className="block text-sm">{decision.row.title}</span>
              <span className="block text-xs text-muted-foreground">
                {worldDisplayName(worlds, decision.worldKey)} ·{" "}
                {expanded ? "Hide the card" : "Review and decide"}
              </span>
            </button>
            {expanded ? (
              <DecisionCardFrame gateInstanceKey={decision.row.gateInstanceKey}>
                <StripCard
                  cardKey={key}
                  decideError={state.refused.get(key) ?? null}
                  decidePending={pending}
                  onCommand={(command) => state.decide(key, decision.worldKey, command)}
                  row={decision.row}
                  setAvailable={state.setAvailable}
                  worldKey={decision.worldKey}
                />
                <AnswerKeyHint
                  checking={state.held?.key === key && state.held.phase === "checking"}
                  held={
                    state.held?.key === key && state.held.phase === "holding"
                      ? state.held.outcome
                      : null
                  }
                  keyable={keysMayAnswer(decision.row, state.available.get(key) === true)}
                  onUndo={state.cancelHold}
                  sending={pending}
                  stopped={gateStoppedHere(decision.row)}
                  undone={state.undone === key}
                />
                <button
                  className="mt-2 self-start rounded px-2 py-1 text-xs text-primary hover:bg-accent disabled:opacity-50"
                  data-agent-strip-open-world={decision.row.gateInstanceKey}
                  disabled={opening}
                  onClick={() => onOpen(decision.worldKey, decision.row.gateInstanceKey)}
                  type="button"
                >
                  Open in its World
                </button>
              </DecisionCardFrame>
            ) : null}
          </article>
        );
      })}
      {refusals.map((worldKey) => (
        <p className="text-xs text-warning" data-agent-strip-unreadable={worldKey} key={worldKey}>
          {worldDisplayName(worlds, worldKey)}: its decisions could not be read. Open the World to
          see them.
        </p>
      ))}
      {morePages.map((worldKey) => (
        <button
          className="block w-full rounded-md p-2 text-left text-xs text-primary hover:bg-accent disabled:opacity-50"
          data-agent-strip-more={worldKey}
          disabled={opening}
          key={worldKey}
          onClick={() => onOpen(worldKey)}
          type="button"
        >
          {worldDisplayName(worlds, worldKey)} has more decisions than shown here · Open them
        </button>
      ))}
      {!loading && decisions.length === 0 && refusals.length === 0 && morePages.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nothing waiting for your decision.</p>
      ) : null}
    </section>
  );
}
